import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import ConsentService from '@lifeomic/phc-web-toolkit/src/services/ConsentService';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProjectId';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import {
  AssignableConsent,
  getUnassignedAssignableConsents,
} from './assignableConsents';

const consentService = new ConsentService();

const messages = defineMessages({
  assignSuccess: {
    id: 'patientViewer.subjectConsent.assignSuccess',
    defaultMessage: 'Consent assigned',
  },
  assignFailure: {
    id: 'patientViewer.subjectConsent.assignFailure',
    defaultMessage: 'Error occurred assigning consent',
  },
});

export const useAssignableConsents = ({
  patientId,
  onConsentAssigned,
}: {
  patientId: string;
  onConsentAssigned: () => void | Promise<void>;
}) => {
  const activeProjectId = useActiveProjectId();
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const [fetchConsentForms, fetchConsentFormsState] = useService(
    consentService.fetchProjectConsents,
  );
  const [fetchPatientConsentDirectives, fetchPatientConsentDirectivesState] =
    useService(consentService.fetchPatientConsentDirectives);
  const [createPatientConsentDirective, createPatientConsentDirectiveState] =
    useService(consentService.createPatientConsentDirective, {
      rethrowErrors: true,
    });

  const loadAssignableConsentData = React.useCallback(() => {
    if (!activeProjectId || !patientId) {
      return;
    }

    fetchConsentForms(activeProjectId);
    fetchPatientConsentDirectives(activeProjectId, patientId);
  }, [
    activeProjectId,
    fetchConsentForms,
    fetchPatientConsentDirectives,
    patientId,
  ]);

  React.useEffect(() => {
    loadAssignableConsentData();
  }, [loadAssignableConsentData]);

  const assignableConsents = React.useMemo(() => {
    const questionnaires =
      fetchConsentFormsState.response?.data?.items ||
      ([] as fhir.Questionnaire[]);
    const directives =
      fetchPatientConsentDirectivesState.response?.data?.items ||
      ([] as fhir.Consent[]);

    return getUnassignedAssignableConsents({
      questionnaires,
      directives,
    });
  }, [
    fetchConsentFormsState.response,
    fetchPatientConsentDirectivesState.response,
  ]);

  const assignConsent = React.useCallback(
    async (consent: AssignableConsent) => {
      if (!activeProjectId || !patientId) {
        return;
      }

      try {
        await createPatientConsentDirective({
          patientId,
          projectId: activeProjectId,
          status: 'proposed',
          matchExactVersions: false,
          identifier: [{ value: consent.consentFormId }],
          id: consent.questionnaireId,
        });
        notify(formatMessage(messages.assignSuccess), 'success');
        await fetchPatientConsentDirectives(activeProjectId, patientId);
        await onConsentAssigned();
      } catch (error) {
        notify(formatMessage(messages.assignFailure), 'error');
      }
    },
    [
      activeProjectId,
      createPatientConsentDirective,
      fetchPatientConsentDirectives,
      formatMessage,
      notify,
      onConsentAssigned,
      patientId,
    ],
  );

  return {
    assignableConsents,
    assignConsent,
    isLoadingAssignableConsents:
      fetchConsentFormsState.isFetching ||
      fetchPatientConsentDirectivesState.isFetching,
    isAssigningConsent: createPatientConsentDirectiveState.isFetching,
  };
};
